import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { CalendarRange, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import creditCardService from '@services/creditCardService';
import type { CreditCardProjection } from '@/types/finance';
import { monthKeyLabel } from '@/utils/competence';
import { formatBRL } from '@/utils/money';
import { ColorChip } from './financeBits';
import { projectionMonthTotals, projectionOthersForView, projectionRowsForView, projectionViewHasData, sumCents, type ProjectionView as View } from './creditCardProjection';

const MONTH_OPTIONS = [6, 12, 18];

/** Coluna de descrição fixa à esquerda no scroll horizontal (fundo por célula para cobrir o que passa por baixo). */
const stickyCellCls = 'sticky left-0 z-10 py-2 px-3 min-w-[9.5rem] sm:min-w-[16rem] max-w-[11rem] sm:max-w-none';

const MoneyCell: React.FC<{ value: number; label?: string; bold?: boolean }> = ({ value, label, bold }) => {
  if (value === 0 && !label) return <td className="text-right py-2 px-3 text-slate-300">–</td>;
  return (
    <td className={clsx('text-right py-2 px-3 whitespace-nowrap tabular-nums', bold && 'font-semibold', value < 0 ? 'text-red-600' : 'text-emerald-700')}>
      {formatBRL(value)}
      {label && <span className="block text-[10px] font-normal text-slate-400">{label}</span>}
    </td>
  );
};

/** Matriz: linhas = compras parceladas e recorrências, "Demais lançamentos" e "Total do mês"; colunas = competências. */
const ProjectionMatrix: React.FC<{ data: CreditCardProjection; view: View }> = ({ data, view }) => {
  const rows = projectionRowsForView(data, view);
  const others = projectionOthersForView(data, view);
  const totals = projectionMonthTotals(data, view);
  const hasOthers = others.some((v) => v !== 0);
  const cardOf = (id: number) => data.cards.find((c) => c.id === id);

  if (data.months.length === 0 || !projectionViewHasData(data, view)) {
    return <p className="text-sm text-slate-500 py-8 text-center">Nenhuma compra parcelada, recorrência ou item previsto nas próximas competências.</p>;
  }

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <table className="min-w-max w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className={clsx(stickyCellCls, 'bg-white text-left font-semibold text-slate-600')}>Despesa</th>
            {data.months.map((m) => (
              <th key={m} className="text-right py-2 px-3 font-semibold text-slate-600 whitespace-nowrap">
                {monthKeyLabel(m)}
              </th>
            ))}
            <th className="text-right py-2 px-3 font-bold text-slate-700 whitespace-nowrap">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((g) => {
            const card = view === null ? cardOf(g.credit_card_id) : undefined;
            return (
              <tr key={g.key} className="border-b border-slate-100 hover:bg-slate-50">
                <td className={clsx(stickyCellCls, 'bg-white')}>
                  <span className="text-slate-800 [overflow-wrap:anywhere]">{g.description}</span>
                  {g.recurring ? (
                    <span className="badge-info ml-2">recorrente</span>
                  ) : (
                    g.repeat_total != null && g.repeat_total > 1 && <span className="ml-2 text-xs text-slate-400">{g.repeat_total}x</span>
                  )}
                  {card && (
                    <span className="block text-xs text-slate-400">
                      <ColorChip color={card.color}>{card.name}</ColorChip>
                    </span>
                  )}
                </td>
                {g.month_amounts.map((v, i) => (
                  <MoneyCell key={data.months[i] ?? i} value={v} label={g.month_labels[i] || undefined} />
                ))}
                <MoneyCell value={g.total_cents} bold />
              </tr>
            );
          })}
          {hasOthers && (
            <tr className="border-b border-slate-100 bg-slate-50/60">
              <td className={clsx(stickyCellCls, 'bg-slate-50 text-slate-500')}>
                Demais lançamentos
                <span className="block text-xs text-slate-400">avulsos e saldo anterior (sem parcelas nem recorrências)</span>
              </td>
              {others.map((v, i) => (
                <MoneyCell key={data.months[i] ?? i} value={v} />
              ))}
              <MoneyCell value={sumCents(others)} bold />
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50 font-semibold border-t border-slate-200">
            <td className={clsx(stickyCellCls, 'bg-slate-50 text-slate-700')}>Total do mês</td>
            {totals.map((v, i) => (
              <MoneyCell key={data.months[i] ?? i} value={v} bold />
            ))}
            <MoneyCell value={sumCents(totals)} bold />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

/**
 * Finanças → Projeção: GET /credit-cards/projection?months= — o que já está comprometido nas
 * faturas dos próximos meses (parcelas, recorrências e itens já lançados), por cartão ou
 * consolidado, com 6/12/18 meses.
 */
export const ProjectionView: React.FC = () => {
  const [months, setMonths] = useState(12);
  const [view, setView] = useState<View>(null);
  const [data, setData] = useState<CreditCardProjection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await creditCardService.getProjection(months));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => {
    load();
  }, [load]);

  const cards = useMemo(() => data?.cards ?? [], [data]);
  const grand = useMemo(() => (data ? sumCents(projectionMonthTotals(data, view)) : 0), [data, view]);
  const viewCard = view === null ? null : cards.find((c) => c.id === view) ?? null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <CalendarRange size={20} className="text-slate-500" /> Projeção de cartões
        </h1>
        <Button variant="outline" size="sm" icon={<RefreshCw />} onClick={load} disabled={loading}>
          Atualizar
        </Button>
      </div>

      <div className="card p-4 grid grid-cols-1 sm:flex sm:flex-wrap sm:items-end gap-3">
        <div className="sm:w-56">
          <label className="text-label block mb-1">Cartão</label>
          <select className="select-field" value={view ?? ''} onChange={(e) => setView(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Todos os cartões</option>
            {cards.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-label block mb-1">Horizonte</label>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden">
            {MONTH_OPTIONS.map((m) => (
              <button key={m} type="button" onClick={() => setMonths(m)} className={clsx('px-3 py-2 text-sm transition-colors', months === m ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:bg-slate-50')}>
                {m} meses
              </button>
            ))}
          </div>
        </div>
        <div className="sm:ml-auto">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Comprometido no horizonte</p>
          <p className={clsx('text-xl font-semibold tabular-nums', grand < 0 ? 'text-red-600' : 'text-slate-800')}>{formatBRL(grand)}</p>
          {viewCard && (
            <Link to={`/finance/cards?open=${viewCard.id}`} className="text-xs text-emerald-700 hover:underline">
              Abrir faturas de {viewCard.name}
            </Link>
          )}
        </div>
      </div>

      <p className="text-small">
        Cada célula é o que já está (ou vai estar) na fatura daquela competência: parcelas de compras ("03/12" = 3ª de 12), recorrências de cartão e os demais itens lançados
        ou importados. Num cartão só, "Total do mês" é a prévia da fatura.
      </p>

      <div className={clsx('card p-2 sm:p-3', loading && 'opacity-60')}>
        {loading && !data ? (
          <p className="text-center text-slate-400 py-10">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        ) : data ? (
          <ProjectionMatrix data={data} view={view} />
        ) : null}
      </div>

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
